'use client';

import React, { useEffect, useState } from 'react';
import * as d3 from 'd3';
import MedBubble, { DataItem } from './MedBubble';

export const colorMap: Record<string, string> = {
  Commercial: '#f97316', //orange-500
  Medicaid: '#f43f5e', // rose-500
  'Medicare Advantage': '#6366f1', // violet-500
  'Medicare FFS': '#3739df', // indigo-500
  Other: '#06b6d4', // cyan-500
  Tricare: '#10b981', // emerald-500
  VA: '#1e40af', // blue-800
};

export const payerColorClassMap: Record<string, string> = {
  Commercial: 'bg-orange-500',
  Medicaid: 'bg-rose-500',
  'Medicare Advantage': 'bg-violet-500',
  'Medicare FFS': 'bg-indigo-500',
  Other: 'bg-cyan-500',
  'Other Government': 'bg-cyan-500',
  Tricare: 'bg-emerald-500',
  VA: 'bg-blue-800',
};

const defaultWidth = 394;
const defaultHeight = 300;

const formatPayerName = (name: string): string => {
  return name === 'Other Government' ? "Other Gov't" : name;
};

interface AdjustedDataItem {
  value: number;
  originalData: DataItem;
}

interface HierarchyRoot {
  children: AdjustedDataItem[];
}

export interface BubbleChartProps {
  data: DataItem[];
  width?: number;
  height?: number;
  circleProps?: React.SVGProps<SVGCircleElement>;
  textProps?: React.SVGProps<SVGTextElement>;
  diameterRange?: [number, number];
}

const MedBubbleChart: React.FC<BubbleChartProps> = ({
  data,
  width = defaultWidth,
  height = defaultHeight,
  circleProps,
  textProps,
  diameterRange,
}) => {
  const [nodes, setNodes] = useState<DataItem[]>([]);

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Process and sort the data by value
    const processedData = data
      .filter((d) => d.value > 0)
      .map((d) => {
        const originalName = d.name;
        return {
          ...d,
          name: formatPayerName(d.name),
          color: colorMap[originalName as keyof typeof colorMap] || '#3bbefe',
          value: d.value,
        };
      })
      .sort((a, b) => b.value - a.value);

    const values = processedData.map((d) => d.value);
    const minVal = d3.min(values) ?? 0;
    const maxVal = d3.max(values) ?? 100;

    // This diameter calculation is no longer used since D3 pack handles sizing
    // Keeping for potential future use or backwards compatibility
    if (diameterRange) {
      const diameterScale = d3
        .scaleSqrt()
        .domain([minVal, maxVal])
        .range(diameterRange);

      processedData.forEach((d) => {
        d.diameter = diameterScale(d.value);
      });
    }

    // Adjust values to ensure small bubbles are readable while keeping proportions
    const maxValue = Math.max(...processedData.map((d) => d.value));

    const adjustedData: AdjustedDataItem[] = processedData.map((d) => {
      // For very small values (like 1%), boost them significantly
      if (d.value <= 2) {
        return {
          value: Math.max(d.value * 5, maxValue * 0.05), // 5x boost or 05% of largest, whichever is bigger
          originalData: d,
        };
      }
      // For other small values, moderate boost
      if (d.value <= 5) {
        return {
          value: d.value * 2,
          originalData: d,
        };
      }
      // Larger values unchanged
      return {
        value: d.value,
        originalData: d,
      };
    });

    // Create hierarchy for D3 pack layout
    const hierarchyData: HierarchyRoot = { children: adjustedData };
    const root = d3
      .hierarchy<any>(hierarchyData)
      .sum((d: any) => {
        // Root node has children, leaf nodes have value
        return 'value' in d ? d.value : 0;
      })
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    const pack = d3
      .pack<any>()
      .size([width, height]) // Use full container dimensions
      .padding(3); // Minimal padding to maintain distribution

    pack(root);

    // Calculate the actual bounds used by D3
    // After pack() is called, nodes have x, y, r properties
    const leafNodes = root.leaves();
    const minX = Math.min(...leafNodes.map((n: any) => n.x - n.r));
    const maxX = Math.max(...leafNodes.map((n: any) => n.x + n.r));
    const minY = Math.min(...leafNodes.map((n: any) => n.y - n.r));
    const maxY = Math.max(...leafNodes.map((n: any) => n.y + n.r));

    const actualWidth = maxX - minX;
    const actualHeight = maxY - minY;

    // Scale to use full container but leave margin for bubble radii
    const margin = 10; // Small margin to prevent clipping
    const scaleX = (width - margin * 2) / actualWidth;
    const scaleY = (height - margin * 2) / actualHeight;
    // Cap so the largest bubble never exceeds 50% of the container's smaller dimension
    const maxDiameter = Math.min(width, height) * 0.5;
    const maxNodeRadius = Math.max(...leafNodes.map((n: any) => n.r));
    const maxScale = maxDiameter / (maxNodeRadius * 2);
    const scale = Math.min(scaleX, scaleY, maxScale);

    // Extract positioned nodes and scale to full container
    const packedNodes: DataItem[] = leafNodes.map((node: any) => {
      const nodeData = node.data as AdjustedDataItem;
      const originalData = nodeData.originalData;

      return {
        ...originalData,
        x:
          (node.x - minX) * scale +
          margin +
          (width - margin * 2 - actualWidth * scale) / 2,
        y:
          (node.y - minY) * scale +
          margin +
          (height - margin * 2 - actualHeight * scale) / 2,
        diameter: node.r * 2 * scale,
      };
    });

    setNodes(packedNodes);
  }, [data, width, height, diameterRange]);

  return (
    <div className="w-full h-full">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-full"
      >
        {/* Subtle background gradient for depth */}
        <defs>
          <radialGradient id="medBubbleGradient" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#f8fafc" stopOpacity={0.3} />
            <stop offset="100%" stopColor="#e2e8f0" stopOpacity={0.1} />
          </radialGradient>
        </defs>
        <rect width={width} height={height} fill="url(#medBubbleGradient)" />

        {/* Render bubbles sorted by size for proper layering */}
        {nodes
          .sort((a, b) => (b.diameter ?? 0) - (a.diameter ?? 0))
          .map((node) => (
            <MedBubble
              key={`bubble-${node.name}-${node.value}`}
              data={node}
              circleProps={{
                ...circleProps,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
                style: {
                  transition: 'all 0.3s ease-in-out',
                  ...circleProps?.style,
                },
              }}
              textProps={{
                ...textProps,
                style: {
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  ...textProps?.style,
                },
              }}
            />
          ))}
      </svg>
    </div>
  );
};

export default MedBubbleChart;
